<template>
  <section id="portfolio" class="portfolio-section">
    <div class="container">
      <p class="pre-label">إبداعاتنا</p>
      <h2 class="section-title">أحدث أعمالنا</h2>
      <div class="gold-divider" />
      <p class="section-subtitle">
        نماذج من مشاريعنا التي نفخر بتقديمها لعملائنا
      </p>

      <!-- Filter tabs -->
      <div class="filter-tabs">
        <button
          v-for="cat in categories"
          :key="cat"
          class="tab-btn"
          :class="{ active: activeCategory === cat }"
          @click="activeCategory = cat"
        >
          {{ cat }}
        </button>
      </div>

      <!-- Portfolio grid -->
      <div class="portfolio-grid">
        <div
          v-for="item in filteredItems"
          :key="item.title"
          class="portfolio-card"
          @mouseenter="item.hovered = true"
          @mouseleave="item.hovered = false"
        >
          <!-- Thumbnail placeholder -->
          <div class="card-thumb" :style="{ background: item.gradient }">
            <span class="thumb-icon">{{ item.icon }}</span>
            <div class="thumb-pattern" />
          </div>

          <!-- Overlay -->
          <div class="card-overlay">
            <h4 class="card-title">{{ item.title }}</h4>
            <p class="card-category">{{ item.category }}</p>
            <a href="#contact" class="card-link">عرض التفاصيل ←</a>
          </div>
        </div>
      </div>

      <div class="view-more">
        <a href="#contact" class="btn-outline-gold">
          اطلب عملك المماثل
          <span>→</span>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, reactive } from "vue";

const activeCategory = ref("الكل");

const categories = ["الكل", "مواقع ويب", "تطبيقات", "جرافيك", "متاجر"];

const items = reactive([
  {
    title: "منصة تعليمية",
    category: "مواقع ويب",
    icon: "🎓",
    gradient: "linear-gradient(135deg,#1a1a4e,#2d2d8e)",
    hovered: false,
  },
  {
    title: "تطبيق توصيل",
    category: "تطبيقات",
    icon: "🚗",
    gradient: "linear-gradient(135deg,#1a3a1a,#2d7a2d)",
    hovered: false,
  },
  {
    title: "هوية شركة عقارية",
    category: "جرافيك",
    icon: "🏢",
    gradient: "linear-gradient(135deg,#3a1a1a,#8a2d2d)",
    hovered: false,
  },
  {
    title: "متجر إلكتروني",
    category: "متاجر",
    icon: "🛍️",
    gradient: "linear-gradient(135deg,#3a2a1a,#8a6d2d)",
    hovered: false,
  },
  {
    title: "داشبورد إداري",
    category: "مواقع ويب",
    icon: "📊",
    gradient: "linear-gradient(135deg,#1a2a3a,#2d5a8a)",
    hovered: false,
  },
  {
    title: "تطبيق لياقة",
    category: "تطبيقات",
    icon: "💪",
    gradient: "linear-gradient(135deg,#2a1a3a,#6a2d8a)",
    hovered: false,
  },
  {
    title: "هوية مطعم",
    category: "جرافيك",
    icon: "🍽️",
    gradient: "linear-gradient(135deg,#1a3a2a,#2d8a6a)",
    hovered: false,
  },
  {
    title: "سوبر ماركت أونلاين",
    category: "متاجر",
    icon: "🛒",
    gradient: "linear-gradient(135deg,#2a3a1a,#5a8a2d)",
    hovered: false,
  },
  {
    title: "نظام حجز مواعيد",
    category: "مواقع ويب",
    icon: "📅",
    gradient: "linear-gradient(135deg,#1a2a4a,#2d4a9a)",
    hovered: false,
  },
]);

const filteredItems = computed(() =>
  activeCategory.value === "الكل"
    ? items
    : items.filter((i) => i.category === activeCategory.value)
);
</script>

<style scoped>
.portfolio-section {
  padding: 7rem 6vw;
  background: var(--bg-dark);
  position: relative;
}

.portfolio-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
}

.container {
  max-width: 1300px;
  margin: 0 auto;
}

.pre-label {
  text-align: center;
  color: var(--gold);
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 3px;
  margin-bottom: 0.5rem;
}

/* Filter tabs */
.filter-tabs {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 3rem;
}

.tab-btn {
  padding: 0.5rem 1.4rem;
  border-radius: 50px;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--gray);
  font-size: 0.9rem;
  font-family: "Cairo", sans-serif;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
}

.tab-btn:hover {
  border-color: var(--gold);
  color: var(--gold);
}

.tab-btn.active {
  background: linear-gradient(135deg, var(--gold), var(--gold-dark));
  color: #000;
  border-color: transparent;
  box-shadow: 0 4px 16px var(--gold-glow);
}

/* Grid */
.portfolio-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.3rem;
}

.portfolio-card {
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  border: 1px solid var(--border);
  transition: all 0.35s ease;
  cursor: pointer;
}

.portfolio-card:hover {
  border-color: var(--gold);
  transform: scale(1.02);
  box-shadow: 0 15px 45px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(212, 175, 55, 0.2);
}

.card-thumb {
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.thumb-icon {
  font-size: 4rem;
  z-index: 1;
  filter: drop-shadow(0 4px 20px rgba(0, 0, 0, 0.5));
}

.thumb-pattern {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.03) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 30px 30px;
}

.card-overlay {
  padding: 1.3rem;
  background: var(--bg-card);
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--white);
  margin-bottom: 0.3rem;
}

.card-category {
  font-size: 0.8rem;
  color: var(--gold);
  margin-bottom: 0.8rem;
}

.card-link {
  font-size: 0.85rem;
  color: var(--gray);
  font-weight: 600;
  transition: color 0.3s;
}

.portfolio-card:hover .card-link {
  color: var(--gold);
}

/* View more */
.view-more {
  text-align: center;
  margin-top: 3.5rem;
}

.btn-outline-gold {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  border: 2px solid var(--gold);
  color: var(--gold);
  padding: 0.85rem 2.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.3s;
}

.btn-outline-gold:hover {
  background: var(--gold-glow);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px var(--gold-glow);
}

@media (max-width: 700px) {
  .portfolio-section {
    padding: 5rem 1.5rem;
  }
  .portfolio-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 480px) {
  .portfolio-grid {
    grid-template-columns: 1fr;
  }
}
</style>
